import {
	ColumnsProps,
	ScrollType,
	buildActionColumn,
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getSelectedRowKeysPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	lockedValidation,
	maxIntValue,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { deviceAdminApi } from '@/api';
import {
	ButtonDropdown,
	EditableCell,
	HelpFactoryComponent,
	ModalConfirmation,
	ModalConfirmationList,
	ModalWarning,
	SearchColumn,
	Select,
} from '@/components/common';
import { SecuredComponents, User, getPermissionErrorMessage } from '@/model/AccountModel';
import {
	BaseColumns,
	PaginationSetting,
	ResponseObject,
	ResponseStatusCode,
	SelectOptions,
	SelectedDeviceKeys,
	SelectedDevicePage,
	SorterConfigEx,
} from '@/model/CommonModel';
import {
	ButtonBuilder,
	DeviceObjectType,
	DeviceSelectedPage,
	HolidayGroup,
	Holidays,
	OptionsButtonBuilder,
	SelectedRowKeyPagination,
	VelocityConfigurationModel,
} from '@/model/DeviceAdminModel';
import { HelpFeature } from '@/model/HelpModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import {
	clearVelocityConfigurationSelectionsAction,
	setHolidaysBy,
	setHolidaysByData,
	setTablePaginationSetting,
	setVelocityConfigurationEditMode,
	setVelocityConfigurationFilterMode,
	setVelocitySelectedRowKeysPaginationAction,
} from '@/store/deviceControl/actions';
import { selectTablePaginationSetting, selectVelocityConfiguration } from '@/store/deviceControl/selectors';
import { CaretDownOutlined, CheckOutlined, PlusOutlined, SettingFilled } from '@ant-design/icons';
import { Button, Form, Table, notification } from 'antd';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { HolidayAlert } from './HolidayAlert/HolidayAlert';
import { HolidayGroupModal } from './HolidayGroupModal/HolidayGroupModal';
import { HolidayModal } from './HolidayModal/HolidayModal';
import { HolidayMoveModal } from './HolidayMoveModal/HolidayMoveModal';
import styles from './holidays.module.scss';

type Props = {};

type ColumnsHolidays = {
	Action: React.ReactNode;
	Name: React.ReactNode;
	T1: React.ReactNode;
	T2: React.ReactNode;
	T3: React.ReactNode;
	T4: React.ReactNode;
} & BaseColumns;

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Holiday);
const scroll: ScrollType = { x: 379, y: 312 };
const maxLength: number = 32;
const deviceObjectType: DeviceObjectType = DeviceObjectType.Holidays;
const topicId: string = 'DigitracHolidays';

const createButtonOptions = (
	hasMoreThanOne: boolean,
	isItemOnSamePage: boolean,
	isHolidayGroupAction: boolean = false,
	isNoCustomGroups: boolean = false
): OptionsButtonBuilder<string> => {
	const options = [
		{
			//This only uses can view permissions since we disabled the Save button on the component itself, if it doesn't have permissions
			label: _('Edit'),
			disabled: !componentPermission.canView || hasMoreThanOne,
			id: 'holidaysTableActionDropdownEdit',
			value: 'edit',
			title: getPermissionErrorMessage(componentPermission.canView, false, hasMoreThanOne),
		},
		{
			label: _('Rename'),
			id: 'holidaysTableActionDropdownRename',
			disabled: !componentPermission.canUpdate || hasMoreThanOne || !isItemOnSamePage,
			title: getPermissionErrorMessage(componentPermission.canUpdate, false, hasMoreThanOne),
			value: 'rename',
		},
		{
			label: _('MoveHolidayGroups'),
			id: 'holidaysTableActionDropdownMoveHolidayGroup',
			disabled: !componentPermission.canUpdate || isNoCustomGroups,
			title: getPermissionErrorMessage(componentPermission.canUpdate, true, hasMoreThanOne),
			value: 'move',
		},
		{
			label: _('Delete'),
			id: 'holidaysTableActionDropdownDelete',
			disabled: !componentPermission.canDelete,
			title: getPermissionErrorMessage(componentPermission.canDelete),
			value: 'delete',
		},
	];

	if (isHolidayGroupAction) {
		options.splice(1, 2);
	}

	return {
		labelOrIcon: isHolidayGroupAction ? <SettingFilled /> : '...',
		options: options,
	};
};

const Holidays: React.FC<Props> = () => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();

	const [editingKey, setEditingKey] = useState('');
	const [isItemOnSamePage, setIsItemOnSamePage] = useState<boolean>(false);
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [tableData, setTableData] = useState<ColumnsHolidays[]>([]);
	const [openModal, setOpenModal] = useState(false);
	const [holidayToEdit, setHolidayToEdit] = useState(0);
	const [selectedHolidayPage, setSelectedHolidayPage] = useState<SelectedDevicePage>(null);
	const [isComponentMounting, setIsComponentMounting] = useState<boolean>(true);
	const [holidayGroups, setHolidayGroups] = useState<SelectOptions<string>[]>([]);
	const [selectedHolidayGroup, setSelectedHolidayGroup] = useState<string>('');
	const [openHolidayGroupModal, setOpenHolidayGroupModal] = useState<boolean>(false);
	const [holidayGroupState, setHolidayGroupState] = useState<HolidayGroup>({ Id: 1, Name: '<Default Holidays>' });
	const [holidaysToMove, setHolidaysToMove] = useState<SelectedDeviceKeys[]>([]);
	const [openMoveHolidayModal, setOpenMoveHolidayModal] = useState<boolean>(false);
	const [sorterConfigEx, setSorterConfigEx] = useState<SorterConfigEx>({ columnKey: undefined, order: undefined });

	const redirectPage = useRef<boolean>(false);
	const isSearchPerformed = useRef<boolean>(false);
	const holidayGroupReference = useRef<HolidayGroup>(holidayGroupState);

	const [form] = Form.useForm();
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const { holidays, isEditMode, isFilterMode, selectedRowKeysPagination }: VelocityConfigurationModel = useStoreSelector(selectVelocityConfiguration);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);
	//#endregion

	const setHolidayGroup = (value: HolidayGroup) => {
		holidayGroupReference.current = value;
		setHolidayGroupState(value);
	};

	useEffect(() => {
		setIsComponentMounting(true);
		deviceAdminApi
			.getHolidayGroups()
			.then(response => {
				if (!handleResponse(response)) {
					const groups = response.Entity;
					const defaultGroup = groups.find(g => g.Id === 1);
					batch(() => {
						setHolidayGroups(mapGroups(groups));
						setSelectedHolidayGroup(defaultGroup.Name);
						setHolidayGroup({ Id: defaultGroup.Id, Name: defaultGroup.Name });
					});
				}
			})
			.finally(() => {
				setIsComponentMounting(false);
			});

		return () => {
			clearSelection();
		};
	}, []);

	useEffect(() => {
		const mapRes: ColumnsHolidays[] = createData();
		setTableData(mapRes);
	}, [JSON.stringify(holidays)]);

	useEffect(() => {
		if (isComponentMounting) return;
		const group = holidayGroups.find(g => g.value === selectedHolidayGroup);
		if (group) {
			const groupId = Number(group.id);
			setHolidayGroup({ Id: groupId, Name: group.value });
			loadHolidays({ ...getDefaultPaginationSettings(), PageSize: tablePaginationSetting.PageSize }, groupId);
			setSorterConfigEx({ columnKey: undefined, order: undefined });
			setTimeout(() => {
				resetSearchColumn(true);
			}, 0);
		}
	}, [selectedHolidayGroup]);

	// * This hook is responsible for loading the previously edited command set
	useEffect(() => {
		if (redirectPage.current) {
			setRedirectPage(false);
			if (selectedRowKeys.length === 1) {
				const holidayId = Number(selectedRowKeys?.[0]);
				getSelectedHolidayPage(holidayId);
			}
		}
	}, [redirectPage.current]);

	useEffect(() => {
		if (selectedHolidayPage) {
			setRedirectPage(false);
			setIsItemOnSamePage(true);

			const { deviceId, pageNumber } = selectedHolidayPage;

			batch(() => {
				dispatch(setVelocitySelectedRowKeysPaginationAction([{ Id: deviceId.toString(), PaginationPage: pageNumber }]));
				dispatch(setHolidaysBy({ ...tablePaginationSetting, PageNumber: pageNumber, SearchedValue: '' }, holidayGroupReference.current.Id));
			});

			setSelectedHolidayPage(null);
		}
	}, [JSON.stringify(selectedHolidayPage)]);

	// * This hook ensure that always return a valid page number when no values has returned
	useEffect(() => {
		if (tablePaginationSetting.TotalItems === 0 && tablePaginationSetting.PageNumber > 1) {
			const { PageNumber: currentPage } = tablePaginationSetting;
			dispatch(
				setHolidaysBy(
					{
						...tablePaginationSetting,
						PageNumber: currentPage - 1,
					},
					holidayGroupReference.current.Id
				)
			);
		}
	}, [tablePaginationSetting]);

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setIsItemOnSamePage(isSamePage);
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const mapGroups = (groups: HolidayGroup[]): SelectOptions<string>[] => {
		if (groups?.length > 0) {
			return groups.map<SelectOptions<string>>((g: HolidayGroup) => ({
				id: g.Id.toString(),
				label: g.Name,
				value: g.Name,
			}));
		}

		return [];
	};

	const loadHolidays = (pagination: PaginationSetting, filterByHolidayGroupId) => {
		setIsTableLoading(true);
		deviceAdminApi
			.getHolidaysBy({ ...pagination }, filterByHolidayGroupId)
			.then(response => {
				if (!handleResponse(response)) {
					const {
						Entity: { Holidays, TotalItemsPaginated },
					} = response;
					batch(() => {
						dispatch(setHolidaysByData(Holidays));
						dispatch(setTablePaginationSetting({ ...pagination, TotalItems: TotalItemsPaginated }));
						clearSelection();
					});
				}
			})
			.finally(() => {
				setIsTableLoading(false);
			});
	};

	const getSelectedHolidayPage = async (deviceId: number) => {
		const deviceSelectedPage: DeviceSelectedPage = {
			DeviceId: deviceId,
			DeviceObjectType: deviceObjectType,
			IsMasterDoorGroup: false,
		};
		const response = await deviceAdminApi.getVelocityDeviceSelectedPage(
			deviceSelectedPage,
			{ ...tablePaginationSetting, SearchedValue: '' },
			holidayGroupReference.current.Id
		);
		const { Entity: pageNumber } = response;

		setSelectedHolidayPage({ deviceId, pageNumber });
	};

	const clearSelection = () => {
		if (redirectPage.current) {
			return;
		}
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: deviceObjectType }));
		setSelectedRowKeys([]);
	};

	const onRemoveHolidayLocks = async (holidayID: number): Promise<void> => {
		await deviceAdminApi.unlockComponentDeviceAdmin(holidayID, SecuredComponents.Holiday);
	};

	const onDeleteHolidayRequest = async (holiday: Holidays): Promise<void> => {
		const response = await deviceAdminApi.deleteHoliday(holiday);
		if (handleResponse(response)) {
			return;
		}

		if (response.Entity?.length > 0) {
			ModalWarning({
				okText: _('Ok'),
				content: <HolidayAlert data={response.Entity} />,
				title: _('HolidaysInUse'),
			});
		}

		onRemoveHolidayLocks(holiday.HolidayID);
	};

	const handleOkConfirmation = async (selectedDeviceKeys: SelectedDeviceKeys[]) => {
		const selectedHolidays = selectedDeviceKeys.map(x => ({ HolidayID: x.Id, Name: x.Name, HolidayGroupId: holidayGroupReference.current.Id } as Holidays));
		for (const holiday of selectedHolidays) {
			await onDeleteHolidayRequest(holiday);
		}
		setSelectedRowKeys([]);
		dispatch(setVelocitySelectedRowKeysPaginationAction([]));
		dispatch(setHolidaysBy(tablePaginationSetting, holidayGroupReference.current.Id));
	};

	const disabled: boolean = editingKey !== '';
	const hasMoreThanOneKeySelected: boolean = selectedRowKeys.length > 1;
	const isNoCustomGroups = holidayGroups.length <= 1;
	const items = createButtonOptions(hasMoreThanOneKeySelected, isItemOnSamePage, false, isNoCustomGroups);
	const groupItems = createButtonOptions(hasMoreThanOneKeySelected, isItemOnSamePage, true);

	const handleOnSearchResults = (searchedValue: string) => {
		if (redirectPage.current) {
			return;
		}
		dispatch(setHolidaysBy({ ...tablePaginationSetting, TotalItems: 0, PageNumber: 1, SearchedValue: searchedValue }, holidayGroupReference.current.Id));
		clearSelection();
	};

	const resetSearchColumn = (forceReset?: boolean) => {
		if (forceReset) {
			setShouldResetSearchColumn(false);
			if (isFilterMode) {
				setTimeout(() => {
					setShouldResetSearchColumn(true);
				}, 0);
			}
		} else if (isFilterMode) {
			setShouldResetSearchColumn(true);
		}
	};

	let columns: ColumnsProps<ColumnsHolidays>[] = [
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			key: 'Name',
			editable: true,
			sorter: !isEditMode,
			sortOrder: sorterConfigEx.columnKey === 'Name' && sorterConfigEx.order,
			...SearchColumn({
				maxLength,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: isEditMode,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
		},
		{
			...buildColumn('1', 'T1', '80px', 'start'),
		},
		{
			...buildColumn('2', 'T2', '80px', 'start'),
		},
		{
			...buildColumn('3', 'T3', '80px', 'start'),
		},
		{
			...buildColumn('4', 'T4', '80px', 'start'),
		},
	];
	if (isEditMode) {
		columns.unshift(buildActionColumn(disabled));
	}

	const onChangeSelection = (key: BaseColumns) => {
		const holidayID = key.key.toString();
		if (selectedRowKeys.findIndex(key => key === holidayID) === -1) {
			const cloneState: Holidays[] = holidays.map(x => (x.HolidayID.toString() === holidayID ? { ...x, checked: true } : { ...x, checked: false }));
			dispatch(setHolidaysByData(cloneState));

			const selectedKeysPagination: SelectedRowKeyPagination = {
				Id: holidayID.toString(),
				PaginationPage: tablePaginationSetting.PageNumber,
			};
			setIsItemOnSamePage(true);
			dispatch(setVelocitySelectedRowKeysPaginationAction([{ ...selectedKeysPagination }]));
			setSelectedRowKeys([holidayID]);
		}
	};

	const isEditing = (record: ColumnsHolidays) => record.key.toString() === editingKey;
	const contextMenuOptions: SelectOptions<string>[] = [...items?.options];

	const mergedColumns = columns.map(col =>
		!col.editable
			? {
					...col,
					onCell: (record: ColumnsHolidays) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
					}),
			  }
			: {
					...col,
					onCell: (record: ColumnsHolidays) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,

						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const getSelectedHolidays = async (): Promise<SelectedDeviceKeys[]> => {
		const response = await deviceAdminApi.getVelocityDevicesBySelectedKeys(deviceObjectType, selectedRowKeys as string[]);

		if (handleResponse(response)) {
			return [];
		}

		return response.Entity;
	};

	const getHoliday = async (holidayId: number): Promise<Holidays> => {
		const response = await deviceAdminApi.getHoliday(holidayId);

		if (handleResponse(response)) {
			return;
		}

		return response.Entity;
	};

	const getSelectedHoliday = async (holidayId: number): Promise<Holidays> => {
		return holidays.find(x => x.HolidayID === holidayId) ?? (await getHoliday(holidayId));
	};

	const handleActionScope = async (key: string) => {
		if (key === 'delete') {
			const selectedHolidays = await getSelectedHolidays();
			ModalConfirmationList({
				type: _('Holiday'),
				data: selectedHolidays,
				onConfirm: () => handleOkConfirmation(selectedHolidays),
			});

			return;
		}
		if (key === 'move') {
			const selectedHolidays = await getSelectedHolidays();
			batch(() => {
				setHolidaysToMove(selectedHolidays);
				setOpenMoveHolidayModal(true);
			});
			return;
		}

		const selectedHolidayId = Number(selectedRowKeys?.[0]);
		const holiday: Holidays = await getSelectedHoliday(selectedHolidayId);
		const lockComponentCondition: boolean = key === 'edit';
		const isLocked: boolean = await lockedValidation(DeviceObjectType.Holidays, holiday.HolidayID, lockComponentCondition);
		if (isLocked) {
			return;
		}

		switch (key) {
			case 'rename':
				const cloneState: Holidays[] = changeStateProps(holiday.HolidayID, { editable: true });
				const isLocked = await lockedValidation(DeviceObjectType.Holidays, holiday.HolidayID);
				if (!isLocked) {
					dispatch(setVelocityConfigurationEditMode(true));
					const findKey: ColumnsHolidays = tableData.find(x => x.key === holiday.HolidayID.toString());
					edit(findKey);
					dispatch(setHolidaysByData(cloneState));
				}
				break;

			case 'edit':
				handleSetModalVisible(true, holiday.HolidayID);
				break;
		}
	};

	const handleActionScopeHolidayGroup = async (key: string) => {
		const groupId = holidayGroups.find(group => group.value.toString() === selectedHolidayGroup).id;
		const group: HolidayGroup = { Id: Number(groupId), Name: selectedHolidayGroup };
		if (key === 'delete') {
			ModalConfirmationList({
				type: _('Delete'),
				translationKey: 'AreYouSureYouWantToThisHolidayGroup',
				data: [group],
				onConfirm: () => {
					deviceAdminApi.getHolidayGroupControllerCount(group.Id).then(response => {
						if (!handleResponse(response)) {
							const controllerCount = response.Entity;
							const messages: string[] = _('DeleteHolidayGroupMessage').split('%%2');
							const mainMessage = messages[0].replace('%%1', controllerCount.toString());
							const continueMessage = messages[1];
							ModalConfirmation({
								onConfirm: () => {
									deviceAdminApi.deleteHolidayGroup(group, controllerCount).then(response => {
										if (!handleResponse(response)) {
											deviceAdminApi.getHolidayGroups().then(groupResponse => {
												if (!handleResponse(groupResponse)) {
													const groups = groupResponse.Entity;
													const defaultGroup = groups.find(g => g.Id === 1);
													batch(() => {
														setHolidayGroups(mapGroups(groups));
														setSelectedHolidayGroup(defaultGroup.Name);
													});
												}
											});
										}
									});
								},
								content: (
									<div>
										{mainMessage}
										<br />
										{continueMessage}
									</div>
								),
							});
						}
					});
				},
			});

			return;
		}

		if (key === 'edit') {
			const isLocked: boolean = await lockedValidation(DeviceObjectType.HolidayGroup, group.Id, true);
			if (isLocked) {
				return;
			}
			batch(() => {
				setHolidayGroup(group);
				setOpenHolidayGroupModal(true);
			});
		}
	};

	const changeStateProps = (deviceId: number, holiday: Partial<Holidays>): Holidays[] => {
		const cloneState: Holidays[] = [...holidays];
		const index = cloneState.findIndex(x => x.HolidayID === deviceId);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...holiday });
		}

		return cloneState;
	};

	const edit = (record: ColumnsHolidays) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.key.toString());
	};

	const handleOnSaveEditRow = async (deviceId: number) => {
		const isLocked: boolean = await lockedValidation(DeviceObjectType.Holidays, deviceId, false);
		if (isLocked) {
			setEditingKey('');
			const cloneState: Holidays[] = changeStateProps(deviceId, { editable: false });
			dispatch(setHolidaysByData(cloneState));
			dispatch(setVelocityConfigurationEditMode(false));
			return;
		}

		const name: string = form.getFieldValue('Name');
		const nameError: ResponseObject = await deviceAdminApi.renameDevice(DeviceObjectType.Holidays, name.trim(), deviceId);
		if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
			setEditingKey('');
			dispatch(setVelocityConfigurationEditMode(false));
			dispatch(setHolidaysBy(tablePaginationSetting, holidayGroupReference.current.Id));
			return;
		}

		setEditingKey('');
		dispatch(setVelocityConfigurationEditMode(false));
		resetSearchColumn(true);
		setRedirectPage(true);
		dispatch(setHolidaysBy(tablePaginationSetting, holidayGroupReference.current.Id));
	};

	const handleOnCancelEditRow = (deviceId: number) => {
		const cloneState: Holidays[] = changeStateProps(deviceId, { editable: false });
		dispatch(setHolidaysByData(cloneState));
		dispatch(setVelocityConfigurationEditMode(false));
		setEditingKey('');
	};

	const getSelectedRowKeysForSelectAll = async (newSelectedRowKeys: React.Key[]): Promise<React.Key[]> => {
		const response = await deviceAdminApi.getHolidaysBy(
			{ ...tablePaginationSetting, PageNumber: 1, PageSize: tablePaginationSetting.TotalItems ?? maxIntValue },
			holidayGroupReference.current.Id
		);
		let holidaysIds = [];
		if (!handleResponse(response)) {
			holidaysIds = response.Entity.Holidays.map<string>(x => x.HolidayID.toString());
		}

		return getUniqueValuesArray(newSelectedRowKeys, holidaysIds);
	};

	const handleChange = (selectedRowKeys: React.Key[]) => {
		const cloneState: Holidays[] = holidays.map(x => ({ ...x, checked: selectedRowKeys.findIndex(r => r === x.HolidayID) !== -1 }));
		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			selectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(selectedRowKeys);
	};

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys = await getSelectedRowKeysForSelectAll(selectedRowKeys);

		const cloneState: Holidays[] = holidays.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.HolidayID?.toString()) !== -1 }));
		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(newSelectedRowKeys);
		setIsTableLoading(false);
	};

	const handleSelectInvert = () => {
		const dataKeys = holidays.map<React.Key>(x => x.HolidayID?.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		const cloneState: Holidays[] = holidays.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.HolidayID?.toString()) !== -1,
		}));

		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const setRedirectPage = (value: boolean) => {
		redirectPage.current = value;
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleSetModalVisible = (visible: boolean, holiday?: number) => {
		setHolidayToEdit(holiday);
		setOpenModal(visible);
	};

	const handleSetGroupModalVisible = (visible: boolean) => {
		setOpenHolidayGroupModal(visible);
	};

	const createActionElement = (holiday: Holidays): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (holiday.editable) {
			const holidayID = holiday.HolidayID;

			content = (
				<>
					<Button id={`holidaysTableActionDropdownSaveButton-${holidayID}`} key='save' type='primary' onClick={() => handleOnSaveEditRow(holidayID)}>
						{_('Save')}
					</Button>
					<Button id={`holidaysTableActionDropdownCancelButton-${holidayID}`} key='cancel' onClick={() => handleOnCancelEditRow(holidayID)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}

		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): ColumnsHolidays[] => {
		if (holidays.length > 0) {
			return holidays.map<ColumnsHolidays>((data: Holidays) => ({
				key: data.HolidayID.toString(),
				Action: createActionElement(data),
				Name: data.Name,
				T1: data.T1 === 1 ? <CheckOutlined /> : null,
				T2: data.T2 === 1 ? <CheckOutlined /> : null,
				T3: data.T3 === 1 ? <CheckOutlined /> : null,
				T4: data.T4 === 1 ? <CheckOutlined /> : null,
			}));
		}

		return [];
	};

	const buttonsBuilder = (button: ButtonBuilder[]): React.ReactNode =>
		button.map(x => (
			<Button id={x.id} key={x.label} onClick={x.onClick} disabled={x.disabled} title={x.title} type='primary'>
				{x.icon} {x.label}
			</Button>
		));

	const buttons: ButtonBuilder[] = [
		{
			label: _('AddNewHoliday'),
			icon: <PlusOutlined />,
			disabled: !componentPermission.canAdd || isEditMode,
			title: getPermissionErrorMessage(componentPermission.canAdd),
			onClick: () => handleSetModalVisible(true, undefined),
			id: 'holidaysAddHolydayButton',
		},
		{
			label: _('AddNewHolidayGroup'),
			icon: <PlusOutlined />,
			disabled: !componentPermission.canAdd || isEditMode,
			title: getPermissionErrorMessage(componentPermission.canAdd),
			onClick: () => {
				setHolidayGroup({ Id: 0, Name: '' });
				handleSetGroupModalVisible(true);
			},
			id: 'holidaysAddNewHolidayGroupButton',
		},
	];

	const rowSelection: TableRowSelection<ColumnsHolidays> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `holidaysTableCheckbox-${record.key}`,
			disabled,
			children: <label htmlFor={`holidaysTableCheckbox-${record.key}`} className={styles.srOnly}>{`${_('SelectHoliday')}`}</label>,
		}),
		selectedRowKeys,
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<ColumnsHolidays>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});
		setSorterConfigEx({ columnKey: sorter.columnKey?.toString(), order: sorter.order });

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			const holidayGroup = holidayGroups.find(group => group.value.toString() === selectedHolidayGroup);
			const holidayGroupId: number = holidayGroup ? Number(holidayGroup.id) : 1;
			dispatch(
				setHolidaysBy(
					{ ...tablePaginationSetting, PageNumber: current, PageSize: pageSize, SortDirection: sortOrder, SortField: sortField.toString() },
					holidayGroupId
				)
			);
		}
	};

	const refreshHolidayGroupsAfterSave = () => {
		deviceAdminApi.getHolidayGroups().then(response => {
			if (!handleResponse(response)) {
				const groups = response.Entity;
				const selGroupId = holidayGroups.find(hg => hg.value.toString() === selectedHolidayGroup)?.id;
				const selGroup = groups.find(g => g.Id === Number(selGroupId));
				batch(() => {
					setHolidayGroups(mapGroups(groups));
					setSelectedHolidayGroup(selGroup.Name);
					setHolidayGroup(selGroup);
				});
			}
		});
	};

	const disabledActionButtons = disabled || selectedRowKeys.length === 0;

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<ButtonDropdown
					id={'holidaysTableActionDropdown'}
					disabled={disabledActionButtons}
					menuOptions={items.options}
					icon={<CaretDownOutlined />}
					labelIcon={items.labelOrIcon}
					onClickOption={handleActionScope}
				/>
				{buttonsBuilder(buttons)}
			</div>
			<div className={styles.buttonsContainer}>
				<div className={styles.holidayGroupContainer}>
					<label htmlFor={'holidayGroupSelection'}>{`${_('ViewGroup')}:`}</label>
					<Select
						id='holidayGroupSelection'
						className={styles.holidayGroupSelect}
						options={holidayGroups}
						value={selectedHolidayGroup}
						onChange={(e: string) => setSelectedHolidayGroup(e)}
					/>
					<ButtonDropdown
						id='holidaysGroupActionDropdown'
						icon={<CaretDownOutlined />}
						disabled={holidayGroupReference.current?.Id === 1}
						menuOptions={groupItems.options}
						labelIcon={groupItems.labelOrIcon}
						onClickOption={handleActionScopeHolidayGroup}
					/>
				</div>
				<HelpFactoryComponent helpFeature={HelpFeature.DigitracHolidays} topicId={topicId} size='medium' className={styles.helpButton} />
			</div>
			<Form form={form} component={false}>
				<Table
					id='holidayTable'
					columns={mergedColumns as ColumnsType<ColumnsHolidays>}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					onRow={(record, rowIndex) => {
						return {
							id: `holidaysTableRow${rowIndex}`,
						};
					}}
					dataSource={tableData}
					pagination={pagination}
					scroll={scroll}
					size='small'
					className={styles.statusWidgetTable}
					rowSelection={rowSelection}
					onChange={handleOnChangeTable}
					loading={isTableLoading}
				/>
			</Form>
			{openModal && (
				<HolidayModal
					holidayId={holidayToEdit}
					onSetVisible={() => handleSetModalVisible(false)}
					setShouldResetSearchColumn={() => resetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					holidayGroupId={holidayGroups ? Number(holidayGroups.find(hg => hg.value === selectedHolidayGroup).id) : 1}
				/>
			)}
			{openHolidayGroupModal && (
				<HolidayGroupModal
					holidayGroup={holidayGroupReference.current}
					onConfirm={() => {
						handleSetGroupModalVisible(false);
						refreshHolidayGroupsAfterSave();
					}}
					onCancel={() => {
						handleSetGroupModalVisible(false);
						const group = holidayGroups.find(hg => hg.value === selectedHolidayGroup);
						if (group) {
							setHolidayGroup({ Id: Number(group.id), Name: group.value });
						}
					}}
				/>
			)}
			{openMoveHolidayModal && (
				<HolidayMoveModal
					holidays={holidaysToMove}
					currentHolidayGroupId={holidayGroupReference.current.Id}
					holidayGroupOptions={holidayGroups.filter(group => group.value !== selectedHolidayGroup)}
					onConfirm={() => {
						batch(() => {
							setOpenMoveHolidayModal(false);
							dispatch(setHolidaysBy({ ...tablePaginationSetting }, holidayGroupReference.current.Id));
							clearSelection();
						});
					}}
					onCancel={() => setOpenMoveHolidayModal(false)}
				/>
			)}
		</div>
	);
};

export { Holidays };
